import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  queryKeys as queryKeysPenilik,
  handleError,
  validateStatus,
} from 'api/kinerja/penilik-pegawai/config'
import { apiPaths, queryKeys } from 'api/kinerja/tim-penilai-penilik/config'
import { useMutationApi, useQueryApi } from 'utils/api'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type {
  PraktikKinerjaFormName,
  SubmissionResponse,
} from 'types/kinerja-penilik-pegawai'
import type { AxiosError } from 'axios'
import type {
  PelaksanaanFilterState,
  PelaksanaanList,
  PelaksanaanFilter,
  PelaksanaanSummary,
  PenilaianPerilakuKerjaOptions,
  PenilaianPerilakuKerjaSubmissionBody,
  PenilaianPerilakuKerjaSubmissionResponse,
  SubmitRatingDiskusiRefleksiTindakLanjutBody,
  PenilaianKSSurveyResults,
} from 'types/kinerja-tim-penilai-penilik'

export function useAPIGetPelaksanaanPerilakuKerjaPenilaianOptions({
  ...options
}: UseQueryOptions<PenilaianPerilakuKerjaOptions>) {
  return useQueryApi<PenilaianPerilakuKerjaOptions>(
    `${apiPaths.pelaksanaan}/perilaku-kerja/options`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA_TK_OPTIONS],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        staleTime: 60 * 60 * 1000, // 1 hour
        cacheTime: 60 * 60 * 1000, // 1 hour
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISubmitPenilaianPerilakuKerja({
  skpId,
}: {
  skpId: string
}) {
  const queryClient = useQueryClient()

  return useMutationApi<
    PenilaianPerilakuKerjaSubmissionResponse,
    AxiosError,
    PenilaianPerilakuKerjaSubmissionBody
  >(`${apiPaths.pelaksanaan}/perilaku-kerja/${skpId}`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled(_res, _err, { skpId }) {
        queryClient.invalidateQueries([
          queryKeysPenilik.API_GET_PELAKSANAAN_PERILAKU_KERJA,
          skpId,
        ])
      },
    },
  })
}

export function useAPIGetPelaksanaanFilter(
  options?: UseQueryOptions<PelaksanaanFilter>
) {
  const { period } = usePeriodContext()

  return useQueryApi<PelaksanaanFilter>(
    apiPaths.pelaksanaanListFilter(period.id),
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_LIST_SUMMARY, period.id],
      axiosConfig: {
        validateStatus,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanList({
  filter,
  ...options
}: {
  filter: PelaksanaanFilterState
} & UseQueryOptions<PelaksanaanList>) {
  const { period } = usePeriodContext()

  return useQueryApi<PelaksanaanList>(
    apiPaths.pelaksanaanList(period.id, filter),
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_LIST, period.id, filter],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanSummary({
  skpId,
  ...options
}: {
  skpId: string
} & UseQueryOptions<PelaksanaanSummary>) {
  return useQueryApi<PelaksanaanSummary>(apiPaths.pelaksanaanSummary(skpId), {
    queryKey: [queryKeys.API_GET_PELAKSANAAN_SUMMARY, skpId],
    axiosConfig: {
      validateStatus,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export const useAPISubmitRatingPraktikKinerja = ({
  skpId,
  formName,
}: {
  skpId: string
  formName: PraktikKinerjaFormName
}) => {
  const queryClient = useQueryClient()

  return useMutationApi<
    SubmissionResponse,
    AxiosError,
    SubmitRatingDiskusiRefleksiTindakLanjutBody
  >(`${apiPaths.pelaksanaan}/praktik-kinerja/${skpId}/rating`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeysPenilik.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
          skpId,
          formName,
        ])
        queryClient.invalidateQueries([
          queryKeysPenilik.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY,
          skpId,
        ])
      },
    },
  })
}

export function useAPIGetKSSurveyResults({
  skpId,
  ...options
}: {
  skpId: string
} & UseQueryOptions<PenilaianKSSurveyResults>) {
  return useQueryApi<PenilaianKSSurveyResults>(
    `${apiPaths.pelaksanaan}/praktik-kinerja/${skpId}/survey-hasil`,
    {
      queryKey: [
        queryKeys.API_GET_PELAKSANAAN_PRAKTIK_KINERJA_HASIL_SURVEY_KS,
        skpId,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}
